import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessageCircle, BookOpen, Calendar, X } from 'lucide-react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { ForumAvatar } from './ForumAvatar';

import defaultAvatar1 from '@/assets/avatars/default-1.png';
import defaultAvatar2 from '@/assets/avatars/default-2.png';
import defaultAvatar3 from '@/assets/avatars/default-3.png';
import defaultAvatar4 from '@/assets/avatars/default-4.png';
import defaultAvatar5 from '@/assets/avatars/default-5.png';
import defaultAvatar6 from '@/assets/avatars/default-6.png';
import defaultAvatar7 from '@/assets/avatars/default-7.png';
import defaultAvatar8 from '@/assets/avatars/default-8.png';
import defaultAvatar9 from '@/assets/avatars/default-9.png';
import defaultAvatar10 from '@/assets/avatars/default-10.png';

const DEFAULT_AVATARS = [
  defaultAvatar1,
  defaultAvatar2,
  defaultAvatar3,
  defaultAvatar4,
  defaultAvatar5,
  defaultAvatar6,
  defaultAvatar7,
  defaultAvatar8,
  defaultAvatar9,
  defaultAvatar10,
];

interface UserStats {
  display_name: string;
  avatar_url: string | null;
  default_avatar: number | null;
  created_at: string;
  topic_count: number;
  reply_count: number;
  word_suggestion_count: number;
  clue_suggestion_count: number;
}

interface ForumUserProfileModalProps {
  userId: string | null;
  userName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ForumUserProfileModal({ userId, userName, isOpen, onClose }: ForumUserProfileModalProps) {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['forum-user-stats', userId],
    queryFn: async (): Promise<UserStats | null> => {
      if (!userId) return null;

      // Fetch basic profile info - will return empty if RLS blocks it
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name, avatar_url, default_avatar, created_at')
        .eq('id', userId)
        .maybeSingle();

      // Count forum topics
      const { count: topicCount } = await supabase
        .from('forum_topics')
        .select('*', { count: 'exact', head: true })
        .eq('author_id', userId)
        .eq('is_deleted', false);

      // Count forum replies
      const { count: replyCount } = await supabase
        .from('forum_replies')
        .select('*', { count: 'exact', head: true })
        .eq('author_id', userId)
        .eq('is_deleted', false);

      // Count approved word suggestions
      const { count: wordCount } = await supabase
        .from('word_suggestions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'approved');

      // Count approved clue suggestions
      const { count: clueCount } = await supabase
        .from('clue_suggestions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'approved');

      return {
        display_name: profile?.display_name || userName,
        avatar_url: profile?.avatar_url || null,
        default_avatar: profile?.default_avatar || null,
        created_at: profile?.created_at || '',
        topic_count: topicCount || 0,
        reply_count: replyCount || 0,
        word_suggestion_count: wordCount || 0,
        clue_suggestion_count: clueCount || 0,
      };
    },
    enabled: isOpen && !!userId,
  });

  const getAvatarSrc = () => {
    if (stats?.avatar_url) {
      return stats.avatar_url;
    }
    if (stats?.default_avatar && stats.default_avatar >= 1 && stats.default_avatar <= 10) {
      return DEFAULT_AVATARS[stats.default_avatar - 1];
    }
    return DEFAULT_AVATARS[0];
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="sr-only">Användarprofil</DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex flex-col items-center gap-4 py-6">
            <Skeleton className="h-24 w-24 rounded-full" />
            <Skeleton className="h-6 w-32" />
            <div className="grid grid-cols-2 gap-4 w-full mt-4">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
          </div>
        ) : stats ? (
          <div className="flex flex-col items-center gap-4 py-2">
            {/* Large profile picture */}
            <div className="relative">
              <img
                src={getAvatarSrc()}
                alt={stats.display_name}
                className="h-24 w-24 rounded-full object-cover ring-4 ring-muted"
              />
            </div>

            {/* Display name */}
            <h2 className="text-xl font-semibold text-foreground">{stats.display_name}</h2>

            {/* Join date */}
            {stats.created_at && (
              <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                Medlem sedan {format(new Date(stats.created_at), 'MMMM yyyy', { locale: sv })}
              </p>
            )}

            {/* Statistics grid */}
            <div className="grid grid-cols-2 gap-3 w-full mt-2">
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-foreground">{stats.topic_count}</div>
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <MessageCircle className="h-3 w-3" />
                  Trådar
                </div>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-foreground">{stats.reply_count}</div>
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <MessageCircle className="h-3 w-3" />
                  Svar
                </div>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-foreground">{stats.word_suggestion_count}</div>
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <BookOpen className="h-3 w-3" />
                  Ord tillagda
                </div>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-foreground">{stats.clue_suggestion_count}</div>
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <BookOpen className="h-3 w-3" />
                  Ledtrådar tillagda
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <p>Kunde inte ladda profilinformation.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
